import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import Modal from 'components/shared/Modal';
import React, { SyntheticEvent, useCallback, useContext, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { defaultFormProps, getRequiredValidationRule, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { ModalProps } from 'store/types/ComponentProps';
import CompanyService from 'services/api/CompanyService';
import { useSnackbar } from 'notistack';
import JobFunction from 'store/types/JobFunction';
import CompanyUser from 'store/types/CompanyUser';
import UserNameFormSection from 'components/profile/UserNameFormSection';
import EmailFormItem from 'components/profile/EmailFormItem';
import PhoneFormItem from 'components/profile/PhoneFormItem';
import { CompanyUserFormValues } from 'store/types/FormValues';
import jobFunction from 'store/types/JobFunction';
import { UserContext } from 'components/UserGuard';
import TitleFormItem from 'components/profile/TitleFormItem';
import SelectOption from 'store/types/SelectOption';
import { MemberClassification } from 'store/types/Company';
import DirectoryStatus from 'store/enums/DirectoryStatus';

import commonStyles from 'styles/common.module.scss';
import styles from './ViewCompanyUserModal.module.scss';

interface ViewCompanyUserModalProps extends ModalProps {
  data: CompanyUser;
  jobFunctionsList?: JobFunction[];
  refetch: () => void;
  companyId: string;
  memberClassification?: MemberClassification[];
  directoryStatusOptions?: SelectOption[];
}

type ViewCompanyUserFormValues = CompanyUserFormValues;

const getDefaultValues = (
  {
    firstName,
    lastName,
    phone,
    email,
    jobFunctionName,
    middleInitial,
    title,
    directoryNetworks,
    directoryStatus,
  }: CompanyUser,
  jobFunctionsList?: jobFunction[]
): ViewCompanyUserFormValues => ({
  firstName,
  lastName,
  middleInitial,
  phone,
  email,
  jobFunctionId: jobFunctionsList?.find(({ name }) => name === jobFunctionName)?.id || '',
  title,
  directoryNetworks: directoryNetworks ?? [],
  directoryStatus: directoryStatus ?? '',
});

const getSelectedLabel = (value: string[] = [], options: MemberClassification[] = []): React.ReactNode =>
  value.length
    ? options
        .filter(({ id }) => value.includes(id))
        .map(({ name }) => name)
        .join(', ')
    : '';

const ViewCompanyUserModal: React.FunctionComponent<ViewCompanyUserModalProps> = ({
  open,
  onClose,
  refetch,
  data,
  companyId,
  jobFunctionsList,
  memberClassification,
  directoryStatusOptions,
}) => {
  const defaultValues = useMemo(() => getDefaultValues(data, jobFunctionsList), [data, jobFunctionsList]);
  const { id } = useContext(UserContext);
  const form = useForm<ViewCompanyUserFormValues>({ ...defaultFormProps, defaultValues });
  const { handleSubmit, formState, control, reset, setValue, watch } = form;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const { directoryStatus } = watch();

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
  }, [defaultValues, onClose, reset]);

  const handleDeleteModalOpen = useCallback(() => {
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteButtonClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (data) {
        setLoading(true);
        (jobFunctionsList?.length
          ? CompanyService.removeCompanyAdmin(companyId, data.id)
          : CompanyService.removeCompanyEmployee(companyId, data.id)
        )
          .then(() => {
            enqueueSnackbar('User successfully deleted', { variant: 'success' });
            setLoading(false);
            refetch();
            handleClose();
          })
          .catch((errorMessage) => {
            setLoading(false);
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          });
      }
    },
    [companyId, data, enqueueSnackbar, handleClose, jobFunctionsList?.length, refetch]
  );

  const handleUpdateUserSubmit = useCallback(
    (formValues: CompanyUserFormValues) => {
      if (data) {
        setLoading(true);
        const { profileId, id } = data;
        (jobFunctionsList?.length
          ? CompanyService.updateCompanyAdminUser(formValues, companyId, profileId, id)
          : CompanyService.updateCompanyEmployeeUser(formValues, companyId, profileId, id)
        )
          .then(() => {
            enqueueSnackbar('User successfully updated', { variant: 'success' });
            setLoading(false);
            refetch();
            handleClose();
          })
          .catch((errorMessage) => {
            setLoading(false);
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          });
      }
    },
    [companyId, data, enqueueSnackbar, handleClose, jobFunctionsList, refetch]
  );

  const isDirectoryPending = useMemo(
    () => directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Submit)?.id === data.directoryStatus,
    [data.directoryStatus, directoryStatusOptions]
  );

  const isDirectoryApproved = useMemo(
    () => directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Approved)?.id === data.directoryStatus,
    [data.directoryStatus, directoryStatusOptions]
  );

  const handleDirectoryStatusClick = useCallback(
    (status: DirectoryStatus) => {
      const statusId = directoryStatusOptions?.find((ds) => ds.name === status)?.id;
      setValue('directoryStatus', statusId, { shouldDirty: true, shouldTouch: true });
    },
    [directoryStatusOptions, setValue]
  );

  return (
    <>
      <FormProvider {...form}>
        <Modal
          open={open}
          onClose={handleClose}
          maxWidth={'sm'}
          title={'View Account Contact'}
          loading={loading}
          actions={
            <>
              <div>
                <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
                  {'Cancel'}
                </Button>
                {id !== data?.profileId && (
                  <Button
                    color={'secondary'}
                    variant={'outlined'}
                    onClick={handleDeleteModalOpen}
                    className={styles.submitButton}
                  >
                    {'Remove Contact'}
                  </Button>
                )}
              </div>
              <Button
                color={'secondary'}
                variant={'contained'}
                type={'submit'}
                disabled={!formState.isValid || !formState.isDirty}
                onClick={handleSubmit(handleUpdateUserSubmit)}
              >
                {'Save'}
              </Button>
            </>
          }
        >
          <Grid {...defaultGridContainerProps}>
            {data.entityId && (
              <Grid {...defaultGridItemProps}>
                <p className={styles.id}>{`ID: ${data.entityId}`}</p>
              </Grid>
            )}
            <UserNameFormSection middleNameVisible={true} />
            <Grid {...defaultGridItemProps} md={6}>
              <EmailFormItem disabled={true} />
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <PhoneFormItem />
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <TitleFormItem />
            </Grid>
            {!!jobFunctionsList?.length && (
              <Grid {...defaultGridItemProps} md={6}>
                <Controller
                  render={({ field }) => (
                    <TextField
                      {...field}
                      {...getValidationProps('jobFunctionName')}
                      label={'Portal Role'}
                      required={true}
                      select={true}
                      disabled={id === data?.profileId}
                    >
                      {jobFunctionsList.map(({ id, name }: JobFunction) => (
                        <MenuItem value={id} key={`job-function-${id}`}>
                          {name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  name={'jobFunctionId'}
                  control={control}
                  rules={{ required: getRequiredValidationRule('job function', true) }}
                />
              </Grid>
            )}
            <Grid {...defaultGridItemProps} md={6}>
              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    className={styles.input}
                    label={'Directory Networks'}
                    disabled={loading}
                    select
                    SelectProps={{
                      displayEmpty: true,
                      multiple: true,
                      renderValue: (selected: any) => getSelectedLabel(selected || [], memberClassification),
                    }}
                  >
                    {memberClassification?.map(({ id, name }) => (
                      <MenuItem value={id} key={`directory-network-option-${id}-${name}`}>
                        <Checkbox checked={field.value?.includes(id)} color={'primary'} size={'small'} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    )) ?? []}
                  </TextField>
                )}
                name={'directoryNetworks'}
                control={control}
              />
            </Grid>
            <Grid {...defaultGridItemProps}>
              <FormControl>
                <FormLabel sx={{ mb: 1 }}>Directory Status</FormLabel>
                <Stack direction={'row'} gap={2}>
                  <Button
                    variant={
                      directoryStatus === directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Hide)?.id
                        ? 'contained'
                        : 'outlined'
                    }
                    color={'primary'}
                    onClick={() => handleDirectoryStatusClick(DirectoryStatus.Hide)}
                  >
                    {DirectoryStatus.Hide}
                  </Button>
                  {isDirectoryApproved ? (
                    <Button variant={'contained'} color={'warning'} disabled>
                      {DirectoryStatus.Approved}
                    </Button>
                  ) : isDirectoryPending ? (
                    <Button variant={'contained'} color={'error'} disabled>
                      {DirectoryStatus.Pending}
                    </Button>
                  ) : (
                    <Button
                      variant={
                        directoryStatus === directoryStatusOptions?.find((ds) => ds.name === DirectoryStatus.Submit)?.id
                          ? 'contained'
                          : 'outlined'
                      }
                      color={'primary'}
                      onClick={() => handleDirectoryStatusClick(DirectoryStatus.Submit)}
                    >
                      {DirectoryStatus.Submit}
                    </Button>
                  )}
                </Stack>
              </FormControl>
            </Grid>
          </Grid>
        </Modal>
      </FormProvider>
      <Modal
        title={'Remove Contact'}
        loading={loading}
        open={deleteModalOpen}
        maxWidth={'xs'}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleDeleteModalClose}>
              {'Cancel'}
            </Button>
            <Button
              type={'submit'}
              variant={'contained'}
              onClick={handleDeleteButtonClick}
              className={commonStyles.dangerButtonContained}
            >
              {'Remove'}
            </Button>
          </>
        }
      >
        <p className={commonStyles.text}>{'Are you sure you want to remove this Contact?'}</p>
      </Modal>
    </>
  );
};

export default ViewCompanyUserModal;
